import { Node as ReactFlowNode } from '@xyflow/react';
import { isNil } from 'lodash';

import { GraphViz_NodeConfigFragment } from '@/modules/graphViz/graphql/GraphVizNodeConfig.fragment.generated';
import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';
import { EntityGraphNodeConfigurationInput } from '@/types/schema';

import { TextNoteNodeData } from '../nodes/TextNoteNode/TextNoteNode';
import { TextNoteNode } from '../types';

export const TEXT_NOTE_ID_PREFIX = 'text-note:';

export const TEXT_NOTE_DEFAULT_WIDTH = 200;
export const TEXT_NOTE_DEFAULT_HEIGHT = 120;

// Use a UUID so IDs are globally unique across page loads — a counter-based
// generator (e.g. lodash.uniqueId) resets to 1 on reload and would collide
// with previously-persisted notes.
export function generateTextNoteId(): string {
  return `${TEXT_NOTE_ID_PREFIX}${globalThis.crypto.randomUUID()}`;
}

export function isTextNoteId(nodeId: string): boolean {
  return nodeId.startsWith(TEXT_NOTE_ID_PREFIX);
}

export function isTextNoteNode(node: { type?: string }): node is TextNoteNode {
  return node.type === 'textNote';
}

/**
 * Reconstitutes a ReactFlow TextNoteNode from a saved node configuration.
 */
export function createTextNoteNodeFromConfig(
  cfg: GraphViz_NodeConfigFragment
): ReactFlowNode<TextNoteNodeData, 'textNote'> {
  return {
    id: cfg.nodeID,
    type: 'textNote' as const,
    position: { x: cfg.xPosition ?? 0, y: cfg.yPosition ?? 0 },
    data: {
      notes: cfg.notes ?? '',
    },
    ...(cfg.width && { width: cfg.width }),
    ...(cfg.height && { height: cfg.height }),
  };
}

/**
 * Serializes a text note ReactFlow node to an EntityGraphNodeConfigurationInput
 * for persistence via GraphQL.
 */
export function textNoteNodeToConfig(
  node: ReactFlowNode<TextNoteNodeData>
): EntityGraphNodeConfigurationInput {
  const { id: nodeID, position, width, height, data } = node;

  return {
    nodeID,
    xPosition: position.x,
    yPosition: position.y,
    hidden: false,
    ...(height && { height }),
    ...(width && { width }),
    ...(typeof data.notes === 'string' && { notes: data.notes }),
    type: GraphVizNodeComponentType.TextNote,
  };
}

/**
 * Filters node configurations to find text note entries (type === 'TEXT_NOTE')
 * that have valid positions.
 */
export function getTextNoteConfigs(
  nodeConfigurations: GraphViz_NodeConfigFragment[]
): GraphViz_NodeConfigFragment[] {
  return nodeConfigurations.filter(
    (cfg) =>
      cfg.type === GraphVizNodeComponentType.TextNote &&
      !isNil(cfg.xPosition) &&
      !isNil(cfg.yPosition)
  );
}
